import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { usersAPI } from '../api/users';

export interface UserQuotaData {
  total_videos: number;
  completed_videos: number;
  total_tasks: number;
  completed_tasks: number;
  success_rate: number;
  membership_level: string;
  quota_remaining: number;
}

interface UserQuotaContextType {
  userQuota: UserQuotaData | null;
  loading: boolean;
  refreshQuota: () => Promise<void>;
  updateQuotaAfterConsumption: (consumedAmount: number) => void;
}

const UserQuotaContext = createContext<UserQuotaContextType | undefined>(undefined);

export const useUserQuota = () => {
  const context = useContext(UserQuotaContext);
  if (!context) {
    throw new Error('useUserQuota must be used within UserQuotaProvider');
  }
  return context;
};

interface UserQuotaProviderProps {
  children: ReactNode;
}

export const UserQuotaProvider: React.FC<UserQuotaProviderProps> = ({ children }) => {
  const [userQuota, setUserQuota] = useState<UserQuotaData | null>(null);
  const [loading, setLoading] = useState(false);
  
  // 重要：缓存键必须包含用户标识，确保不同用户的数据不会混淆
  const getStorageKey = useCallback((): string => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return 'user_quota_cache_no_user';
    }
    // 使用token的前16个字符作为用户标识（安全且唯一）
    const userIdentifier = token.substring(0, 16);
    return `user_quota_cache_${userIdentifier}`;
  }, []);

  // 刷新积分数据
  const refreshQuota = useCallback(async () => {
    try {
      setLoading(true);
      // 未登录时不触发积分刷新，避免无意义的401
      const token = localStorage.getItem('access_token');
      if (!token) {
        // 清除缓存，因为用户已登出
        setUserQuota(null);
        return;
      }
      
      const STORAGE_KEY = getStorageKey();
      
      // 尝试从缓存恢复数据作为回退（但只在有token时）
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.data) {
            setUserQuota(parsed.data as UserQuotaData);
          }
        }
      } catch (e) {
        console.warn('读取积分缓存失败:', e);
      }
      
      // 添加超时保护：如果请求超过 25 秒，使用缓存数据
      const statsPromise = usersAPI.getStats();
      const timeoutPromise = new Promise<UserQuotaData>((_, reject) => {
        setTimeout(() => reject(new Error('请求超时')), 25000);
      });
      
      let stats: UserQuotaData;
      try {
        const statsResult = await Promise.race([statsPromise, timeoutPromise]);
        // 将 UserStats 转换为 UserQuotaData
        stats = {
          total_videos: statsResult.total_videos || 0,
          completed_videos: statsResult.completed_videos || 0,
          total_tasks: statsResult.total_tasks || 0,
          completed_tasks: statsResult.completed_tasks || 0,
          success_rate: statsResult.success_rate || 0,
          membership_level: statsResult.membership_level || 'free',
          quota_remaining: statsResult.quota_remaining || 0
        };
      } catch (error: any) {
        // 超时或请求失败时，使用缓存数据
        console.warn('获取积分数据超时或失败，使用缓存数据:', error.message);
        const STORAGE_KEY = getStorageKey();
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.data) {
              stats = parsed.data as UserQuotaData;
            } else {
              throw new Error('缓存数据格式错误');
            }
          } else {
            throw new Error('没有缓存数据');
          }
        } catch (e) {
          console.warn('读取积分缓存失败:', e);
          // 如果缓存也没有，返回默认值
          stats = {
            total_videos: 0,
            completed_videos: 0,
            total_tasks: 0,
            completed_tasks: 0,
            success_rate: 0,
            membership_level: 'free',
            quota_remaining: 0
          } as UserQuotaData;
        }
      }
      
      setUserQuota(stats);
      // 缓存最新的积分数据，便于下次启动或请求失败时回退
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          data: stats,
          cached_at: Date.now(),
        }));
      } catch (e) {
        // 忽略localStorage错误（比如隐私模式）
        console.warn('缓存积分数据失败:', e);
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        console.warn('刷新积分数据失败(401): 未登录或令牌过期');
        // 401时清除缓存和状态
        setUserQuota(null);
      } else {
        console.error('刷新积分数据失败:', error);
      }
      // 不抛出错误，保持应用稳定运行
      // 若已有旧数据，保持显示；若没有，则尝试从缓存读取
      setUserQuota((prevQuota) => {
        if (!prevQuota) {
          try {
            const STORAGE_KEY = getStorageKey();
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && parsed.data) {
                return parsed.data as UserQuotaData;
              }
            }
          } catch (e) {
            console.warn('读取积分缓存失败:', e);
          }
        }
        return prevQuota;
      });
    } finally {
      setLoading(false);
    }
  }, [getStorageKey]);

  // 积分消耗后的更新（乐观更新）
  const updateQuotaAfterConsumption = useCallback((consumedAmount: number) => {
    setUserQuota((prevQuota) => {
      if (prevQuota) {
        const updated = {
          ...prevQuota,
          quota_remaining: Math.max(0, prevQuota.quota_remaining - consumedAmount)
        };
        // 同步更新缓存
        try {
          const STORAGE_KEY = getStorageKey();
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            data: updated,
            cached_at: Date.now(),
          }));
        } catch (e) {
          console.warn('更新积分缓存失败:', e);
        }
        return updated;
      }
      return prevQuota;
    });
    // 后台异步刷新最新数据
    setTimeout(() => {
      refreshQuota();
    }, 100);
  }, [refreshQuota, getStorageKey]);

  // 组件挂载时初始化数据
  useEffect(() => {
    // 未登录不触发刷新，避免无意义的401噪音
    const token = localStorage.getItem('access_token');
    if (token) {
      // 检查是否是刚登录（通过检查 sessionStorage 标记）
      const isJustLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
      if (isJustLoggedIn) {
        // 清除所有旧缓存，强制刷新
        try {
          const STORAGE_KEY = getStorageKey();
          localStorage.removeItem(STORAGE_KEY);
          // 同时清除所有可能的旧缓存键（兼容旧版本）
          localStorage.removeItem('user_quota_cache');
          // 清除所有以 user_quota_cache_ 开头的缓存
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('user_quota_cache_')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          console.log(`UserQuotaContext: 已清除 ${keysToRemove.length + 1} 个积分缓存`);
          // 重置状态
          setUserQuota(null);
        } catch (e) {
          console.warn('清除积分缓存失败:', e);
        }
        // 清除标记
        sessionStorage.removeItem('justLoggedIn');
      }
      refreshQuota();
    } else {
      // 未登录，清除缓存和状态
      setUserQuota(null);
    }
  }, [refreshQuota, getStorageKey]);

  const value: UserQuotaContextType = {
    userQuota,
    loading,
    refreshQuota,
    updateQuotaAfterConsumption
  };

  return (
    <UserQuotaContext.Provider value={value}>
      {children}
    </UserQuotaContext.Provider>
  );
};
